import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import {
  chartsTooltipClasses,
  ChartsTooltipContainer,
  type ChartsTooltipProps,
  useAxesTooltip,
} from '@mui/x-charts/ChartsTooltip';
import { useCallback, useEffect, useRef, useState } from 'react';
import { tintOnWhite } from '../../theme/colorUtils';
import { r, radii, s } from '../../theme/tokens';
import { uiTokens } from '../../theme/uiTokens';
import { chartBarSizing } from '../theme/chartBarSizing';
import { Box } from './Box';
import { formatChartDateLabel } from './chartDateLabel';
import { getChartTooltipSx } from './chartTooltipStyles';
import { darken } from './colorUtils';
import { useTheme } from './hooks';
import { Text } from './Text';
import { MuiBarChart } from './xCharts';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ChartsAxisData } from '@mui/x-charts/models';
import type { MouseEvent as ReactMouseEvent } from 'react';

export type BarChartDatum = Record<string, string | number>;

type BarSeries = { key: string; label: string; color?: string };
type BarTooltipBreakdownRow = { label: string; value: number; color?: string };
type BarTooltipBreakdown = { total?: number; rows: BarTooltipBreakdownRow[] };
type BarTooltipBreakdownByLabel = Readonly<Record<string, BarTooltipBreakdown | undefined>>;

type BarChartProps = {
  data: BarChartDatum[];
  series: BarSeries[];
  height?: number;
  showLegend?: boolean;
  hideYAxis?: boolean;
  hideXAxisLine?: boolean;
  showHorizontalGrid?: boolean;
  tooltipValueSuffixByLabel?: Readonly<Record<string, string>>;
  tooltipMultiline?: boolean;
  tooltipBreakdownByLabel?: BarTooltipBreakdownByLabel;
  yAxisMin?: number;
  yAxisMax?: number;
  xTickEvery?: number;
  onSelectBar?: (dataIndex: number) => void;
  categoryGapRatio?: number;
  barGapRatio?: number;
  disableInteraction?: boolean;
  disableTooltip?: boolean;
  yReferenceLines?: Array<{ value: number; color: string; label?: string }>;
};

const buildTickLabelInterval = (
  totalPoints: number,
): ((value: string, index: number) => boolean) | 'auto' => {
  const targetTickCount = 8;
  if (totalPoints <= targetTickCount) return 'auto';
  const tickIndexes = new Set<number>();
  for (let tickIndex = 0; tickIndex < targetTickCount; tickIndex += 1) {
    const position = Math.round((tickIndex * (totalPoints - 1)) / (targetTickCount - 1));
    tickIndexes.add(position);
  }
  return (_value: string, index: number) => tickIndexes.has(index);
};

type BreakdownTooltipProps = ChartsTooltipProps & {
  breakdownByLabel: BarTooltipBreakdownByLabel;
};

const BreakdownTooltip = ({
  breakdownByLabel,
  classes,
  sx,
  ...props
}: BreakdownTooltipProps): JSX.Element | null => {
  const tooltipData = useAxesTooltip();
  if (!tooltipData) return null;
  const paperClassName = `${classes?.paper ?? ''} flowqa-bar-tooltip-paper`.trim();
  const tableClassName =
    `${chartsTooltipClasses.table} ${classes?.table ?? ''} flowqa-bar-tooltip-table`.trim();
  const rowClassName = chartsTooltipClasses.row;
  const baseCellClassName =
    `${chartsTooltipClasses.cell} ${classes?.cell ?? ''} flowqa-bar-tooltip-cell`.trim();
  const labelCellClassName = `${baseCellClassName} ${chartsTooltipClasses.labelCell}`.trim();
  const valueCellClassName = `${baseCellClassName} ${chartsTooltipClasses.valueCell}`.trim();

  return (
    <ChartsTooltipContainer classes={classes} sx={sx} {...props}>
      <div className={paperClassName}>
        {tooltipData.map(({ axisId, axisFormattedValue, seriesItems }) => {
          const breakdown = breakdownByLabel[axisFormattedValue];
          const fallbackTotal = seriesItems.reduce((sum, item) => {
            const next = typeof item.value === 'number' ? item.value : Number(item.value);
            return Number.isFinite(next) ? sum + next : sum;
          }, 0);
          const total = breakdown?.total ?? fallbackTotal;
          const rows =
            breakdown?.rows ??
            seriesItems.map((item) => ({
              label: item.formattedLabel ?? '',
              value: Number(item.value),
              color: item.color,
            }));
          return (
            <table key={String(axisId)} className={tableClassName}>
              <thead>
                <tr>
                  <th className={labelCellClassName}>{axisFormattedValue}</th>
                  <td className={valueCellClassName}>{`Total: ${total}`}</td>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={`${axisId}-${row.label}-${index}`} className={rowClassName}>
                    <th className={labelCellClassName}>{row.label}</th>
                    <td className={valueCellClassName}>{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          );
        })}
      </div>
    </ChartsTooltipContainer>
  );
};

export const BarChart = ({
  data,
  series,
  height = 260,
  showLegend = true,
  hideYAxis = false,
  hideXAxisLine = true,
  showHorizontalGrid = true,
  tooltipValueSuffixByLabel,
  tooltipMultiline = false,
  tooltipBreakdownByLabel,
  yAxisMin,
  yAxisMax,
  xTickEvery,
  onSelectBar,
  categoryGapRatio,
  barGapRatio,
  disableInteraction = false,
  disableTooltip = false,
  yReferenceLines = [],
}: BarChartProps): JSX.Element => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [plotBounds, setPlotBounds] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const formatWholeNumber = (value: number | null): string => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '';
    return Number.isInteger(value) ? String(value) : '';
  };
  const formatTooltipValue = (seriesKey: string, value: number | null): string => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '';
    if (seriesKey.toLowerCase() === 'passrate') {
      const rounded = value.toFixed(2).replace(/\.?0+$/, '');
      return `${rounded}%`;
    }
    return formatWholeNumber(value);
  };
  const formatTooltipWithBreakdown = (
    seriesKey: string,
    value: number | null,
    dataIndex?: number,
  ): string => {
    const base = formatTooltipValue(seriesKey, value);
    if (!tooltipValueSuffixByLabel || typeof dataIndex !== 'number' || dataIndex < 0) return base;
    const datum = data[dataIndex];
    const labelValue = datum.label;
    const labelKey = typeof labelValue === 'string' ? labelValue : String(labelValue);
    const suffix = tooltipValueSuffixByLabel[labelKey];
    if (!suffix) return base;
    return `${base} (${suffix})`;
  };
  const palette = [
    uiTokens.statusColors.purple,
    uiTokens.statusColors.green,
    uiTokens.statusColors.orange,
    uiTokens.statusColors.blue,
    uiTokens.statusColors.red,
  ];
  const xLabels = data.map((item) => formatChartDateLabel(String(item.label)));
  const xTickStep =
    typeof xTickEvery === 'number' && Number.isFinite(xTickEvery) && xTickEvery > 1
      ? Math.floor(xTickEvery)
      : null;
  const xTickLabelInterval =
    xTickStep === null
      ? buildTickLabelInterval(xLabels.length)
      : (() => {
          const visibleLabels = new Set(
            xLabels.filter((_, index) => {
              const isStepTick = index % xTickStep === 0;
              const isLast = index === xLabels.length - 1;
              return isStepTick || isLast;
            }),
          );
          return (value: string) => visibleLabels.has(value);
        })();
  const resolvedCategoryGapRatio = categoryGapRatio ?? chartBarSizing.default.categoryGapRatio;
  const resolvedBarGapRatio = barGapRatio ?? chartBarSizing.default.barGapRatio;
  const resolveBarColor = (base: string): string =>
    theme.palette.mode === 'dark' ? darken(base, 0.26) : tintOnWhite(base, 0.7);
  const resolveSeriesColor = (item: BarSeries, index: number): string =>
    resolveBarColor(item.color ?? palette[index % palette.length]);

  const barRadius = radii.lg;
  const hasLegendLabels = series.some((item) => item.label.trim().length > 0);
  const hideLegend = !showLegend || !hasLegendLabels;
  const isPassRateChart = series.some((item) => item.key.toLowerCase() === 'passrate');
  const dataMax = series.reduce((maxValue, item) => {
    const seriesMax = data.reduce((rowMax, row) => {
      const value = Number(row[item.key]);
      return Number.isFinite(value) && value > rowMax ? value : rowMax;
    }, 0);
    return seriesMax > maxValue ? seriesMax : maxValue;
  }, 0);
  const referenceMax = yReferenceLines.reduce((maxValue, line) => {
    if (!Number.isFinite(line.value)) return maxValue;
    return line.value > maxValue ? line.value : maxValue;
  }, 0);
  const computedYAxisMax = Math.max(dataMax, referenceMax);
  const boundedYAxisMax = isPassRateChart ? 100 : Math.max(yAxisMax ?? 0, computedYAxisMax);
  const lastClickRef = useRef<{ dataIndex: number; timestamp: number } | null>(null);
  const lastAxisClickRef = useRef<{ dataIndex: number; timestamp: number } | null>(null);
  const finiteReferenceLines = yReferenceLines.filter((line) => Number.isFinite(line.value));
  const tooltipTrigger = disableTooltip ? 'none' : undefined;
  const tooltipSxParts: Array<
    Record<string, unknown> | ((theme: Theme) => Record<string, unknown>)
  > = [getChartTooltipSx(theme) as Record<string, unknown>];
  if (tooltipMultiline) {
    tooltipSxParts.push({
      '& .MuiChartsTooltip-cell': {
        whiteSpace: 'pre-line',
        verticalAlign: 'top',
      },
      '& .MuiChartsTooltip-valueCell': {
        whiteSpace: 'pre-line',
      },
    });
  }
  if (tooltipBreakdownByLabel) {
    tooltipSxParts.push({
      '& .flowqa-bar-tooltip-paper': {
        backgroundColor: theme.app.colors.surface,
        border: `1px solid ${theme.app.colors.border}`,
        borderRadius: r('md'),
        boxShadow: 'none',
        paddingTop: '6px !important',
        paddingBottom: '2px !important',
        paddingLeft: '6px !important',
        paddingRight: '6px !important',
      },
      '& .flowqa-bar-tooltip-table': {
        borderCollapse: 'separate',
        borderSpacing: 0,
        width: 'auto !important',
        tableLayout: 'auto',
      },
      '& .flowqa-bar-tooltip-table > thead > tr > th, & .flowqa-bar-tooltip-table > thead > tr > td':
        {
          borderBottom: `1px solid ${theme.app.colors.border} !important`,
          boxShadow: 'none !important',
          paddingLeft: '4px !important',
          paddingRight: '4px !important',
          paddingTop: '0 !important',
          paddingBottom: '4px !important',
          verticalAlign: 'middle !important',
          textAlign: 'left !important',
        },
      '& .flowqa-bar-tooltip-table > thead > tr > th': {
        textAlign: 'left !important',
      },
      '& .flowqa-bar-tooltip-table > thead > tr > td': {
        textAlign: 'right !important',
        whiteSpace: 'nowrap',
      },
      '& .flowqa-bar-tooltip-table > tbody > tr > th, & .flowqa-bar-tooltip-table > tbody > tr > td':
        {
          paddingTop: '2px !important',
          paddingBottom: '2px !important',
          verticalAlign: 'middle !important',
          lineHeight: '1.1 !important',
        },
      '& .flowqa-bar-tooltip-table > tbody > tr > th': {
        textAlign: 'left !important',
        whiteSpace: 'nowrap',
      },
      '& .flowqa-bar-tooltip-table > tbody > tr > td': {
        textAlign: 'right !important',
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
      },
      '& .flowqa-bar-tooltip-table > tbody > tr:first-of-type > th, & .flowqa-bar-tooltip-table > tbody > tr:first-of-type > td':
        {
          paddingTop: '8px !important',
        },
      '& .flowqa-bar-tooltip-table > tbody > tr:last-of-type > th, & .flowqa-bar-tooltip-table > tbody > tr:last-of-type > td':
        {
          paddingBottom: '4px !important',
        },
      '& .flowqa-bar-tooltip-cell, & .flowqa-bar-tooltip-table th, & .flowqa-bar-tooltip-table td':
        {
          color: theme.app.colors.text,
          fontSize: theme.typography.body2.fontSize,
          lineHeight: theme.typography.body2.lineHeight,
          fontWeight: theme.typography.fontWeightRegular,
        },
      '& .flowqa-bar-tooltip-table tr': {
        lineHeight: '1.1 !important',
      },
    });
  }
  const tooltipSx = tooltipSxParts as SxProps<Theme>;
  const chartMargin = hideYAxis
    ? { left: 16, right: 16, top: 16, bottom: 14 }
    : { left: -8, right: 16, top: 16, bottom: 14 };
  const hiddenYAxisConfig = hideYAxis
    ? {
        position: 'none' as const,
        width: 0,
        disableLine: true,
        disableTicks: true,
        tickLabelInterval: () => false,
        tickSize: 0,
        tickNumber: 0,
        tickSpacing: 9999,
        tickLabelStyle: { display: 'none', fontSize: 0 },
        labelStyle: { display: 'none', fontSize: 0 },
      }
    : null;

  const handleItemClick = useCallback(
    (_event: ReactMouseEvent<SVGElement>, item: { dataIndex?: number }) => {
      if (!onSelectBar) return;
      const dataIndex = item.dataIndex;
      if (typeof dataIndex !== 'number' || !Number.isFinite(dataIndex)) return;
      const now = Date.now();
      const lastClick = lastClickRef.current;
      if (lastClick?.dataIndex === dataIndex && now - lastClick.timestamp < 400) {
        return;
      }
      lastClickRef.current = { dataIndex, timestamp: now };
      onSelectBar(dataIndex);
    },
    [onSelectBar],
  );
  const handleAxisClick = useCallback(
    (_event: MouseEvent, axisData: ChartsAxisData | null) => {
      if (!onSelectBar) return;
      const dataIndex = axisData?.dataIndex;
      if (typeof dataIndex !== 'number' || !Number.isFinite(dataIndex)) return;
      const now = Date.now();
      const lastAxisClick = lastAxisClickRef.current;
      if (lastAxisClick?.dataIndex === dataIndex && now - lastAxisClick.timestamp < 400) {
        return;
      }
      lastAxisClickRef.current = { dataIndex, timestamp: now };
      onSelectBar(dataIndex);
    },
    [onSelectBar],
  );
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    let frameId = 0;
    const updatePlotBounds = (): void => {
      const svg = container.querySelector('svg');
      if (!(svg instanceof SVGSVGElement)) return;
      const yAxisLine = container.querySelector(
        '.MuiChartsAxis-directionY .MuiChartsAxis-line',
      ) as SVGGraphicsElement | null;
      const xAxisLine = container.querySelector(
        '.MuiChartsAxis-directionX .MuiChartsAxis-line',
      ) as SVGGraphicsElement | null;
      if (!xAxisLine) return;
      const svgRect = svg.getBoundingClientRect();
      const xRect = xAxisLine.getBoundingClientRect();
      const yRect = yAxisLine?.getBoundingClientRect();
      const rawLeft = yRect ? yRect.left - svgRect.left : xRect.left - svgRect.left;
      const rawTop = yRect ? yRect.top - svgRect.top : 16;
      const rawRight = xRect.right - svgRect.left;
      const rawBottom = xRect.top - svgRect.top;
      const svgWidth = svgRect.width;
      const svgHeight = svgRect.height;
      const left = Math.max(0, Math.min(rawLeft, svgWidth));
      const top = Math.max(0, Math.min(rawTop, svgHeight));
      const right = Math.max(left, Math.min(rawRight, svgWidth));
      const bottom = Math.max(top, Math.min(rawBottom, svgHeight));
      const width = Math.max(0, right - left);
      const plotHeight = Math.max(0, bottom - top);
      setPlotBounds({ left, top, width, height: plotHeight });
    };
    const scheduleUpdate = (): void => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(updatePlotBounds);
    };
    scheduleUpdate();
    const resizeObserver = new ResizeObserver(() => {
      scheduleUpdate();
    });
    resizeObserver.observe(container);
    const svg = container.querySelector('svg');
    if (svg instanceof SVGSVGElement) {
      resizeObserver.observe(svg);
    }
    window.addEventListener('resize', scheduleUpdate);
    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      resizeObserver.disconnect();
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [data, hideYAxis, height, series]);
  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height,
        position: 'relative',
        overflow: 'hidden',
        color: theme.app.colors.divider,
        '& .MuiChartsSurface-root': { color: theme.app.colors.divider },
        '& .MuiChartsLegend-root': { display: 'none' },
      }}
    >
      {plotBounds ? (
        <Box
          sx={{
            position: 'absolute',
            left: plotBounds.left,
            top: plotBounds.top,
            width: plotBounds.width,
            height: plotBounds.height,
            backgroundColor: theme.palette.background.paper,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      ) : null}
      <MuiBarChart
        height={height}
        slots={
          tooltipBreakdownByLabel
            ? {
                tooltip: (tooltipProps) => {
                  const entries = Object.entries(tooltipBreakdownByLabel);
                  const breakdownByFormattedLabel = Object.fromEntries(
                    entries.map(([label, breakdown]) => [formatChartDateLabel(label), breakdown]),
                  );
                  return (
                    <BreakdownTooltip
                      {...tooltipProps}
                      breakdownByLabel={breakdownByFormattedLabel}
                    />
                  );
                },
              }
            : undefined
        }
        borderRadius={barRadius}
        onItemClick={onSelectBar && !disableInteraction ? handleItemClick : undefined}
        onAxisClick={onSelectBar && !disableInteraction ? handleAxisClick : undefined}
        series={series.map((item, index) => ({
          data: data.map((row) => Number(row[item.key])),
          label: item.label,
          color: resolveSeriesColor(item, index),
          valueFormatter: (value, context) => {
            const maybeDataIndex =
              typeof context.dataIndex === 'number' ? context.dataIndex : undefined;
            return formatTooltipWithBreakdown(item.key, value, maybeDataIndex);
          },
        }))}
        xAxis={[
          {
            data: xLabels,
            scaleType: 'band',
            categoryGapRatio: resolvedCategoryGapRatio,
            barGapRatio: resolvedBarGapRatio,
            valueFormatter: (value) => formatChartDateLabel(String(value)),
            tickLabelInterval: xTickLabelInterval,
            tickInterval: xLabels,
            tickLabelStyle: { fill: theme.app.colors.textMuted, transform: 'translateY(4px)' },
            tickSize: 6,
          },
        ]}
        yAxis={[
          {
            min: yAxisMin ?? 0,
            max: boundedYAxisMax > 0 ? boundedYAxisMax : undefined,
            tickLabelStyle: hideYAxis
              ? { display: 'none', fontSize: 0 }
              : { fill: theme.app.colors.textMuted },
            tickSize: 0,
            valueFormatter: hideYAxis ? () => '' : formatWholeNumber,
            ...(hiddenYAxisConfig ?? {}),
          },
        ]}
        grid={{ horizontal: showHorizontalGrid, vertical: false }}
        margin={chartMargin}
        slotProps={{ tooltip: { sx: tooltipSx, trigger: tooltipTrigger } }}
        sx={{
          position: 'relative',
          zIndex: 1,
          '& .MuiChartsAxis-tickLabel': { fill: theme.app.colors.textMuted },
          '& .MuiChartsAxis-directionY .MuiChartsAxis-tickLabel': {
            transform: 'translateX(-6px)',
          },
          '& .MuiChartsAxis-directionX .MuiChartsAxis-line, & .MuiChartsAxis-directionX .MuiChartsAxis-line[stroke]':
            {
              stroke: `${theme.app.colors.divider} !important`,
              strokeOpacity: 1,
            },
          '& .MuiChartsAxis-directionX .MuiChartsAxis-tick, & .MuiChartsAxis-directionX .MuiChartsAxis-tick[stroke]':
            {
              stroke: `${theme.app.colors.divider} !important`,
              strokeOpacity: 1,
            },
          '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': {
            stroke: theme.app.colors.divider,
            strokeOpacity: 1,
          },
          '& .MuiChartsGrid-horizontalLine': { stroke: theme.app.colors.divider },
          '& .MuiChartsAxis-line[stroke]': {
            stroke: `${theme.app.colors.divider} !important`,
          },
          '& .MuiChartsAxis-tick[stroke]': {
            stroke: `${theme.app.colors.divider} !important`,
          },
          ...(hideXAxisLine
            ? {
                '& .MuiChartsAxis-directionX .MuiChartsAxis-line, & .MuiChartsAxis-directionX .MuiChartsAxis-line[stroke]':
                  {
                    stroke: 'transparent !important',
                  },
              }
            : null),
          ...(hideYAxis
            ? {
                '& .MuiChartsAxis-directionY .MuiChartsAxis-line, & .MuiChartsAxis-directionY .MuiChartsAxis-line[stroke]':
                  {
                    stroke: 'transparent !important',
                  },
                '& .MuiChartsAxis-directionY .MuiChartsAxis-tick, & .MuiChartsAxis-directionY .MuiChartsAxis-tick[stroke]':
                  {
                    stroke: 'transparent !important',
                  },
                '& .MuiChartsAxis-directionY .MuiChartsAxis-tickLabel': {
                  fill: 'transparent !important',
                },
              }
            : null),
          ...(onSelectBar && !disableInteraction
            ? {
                '& .MuiChartsAxis-directionX': { cursor: 'pointer' },
                '& .MuiChartsAxis-directionX .MuiChartsAxis-tickLabel': { cursor: 'pointer' },
                '& .MuiChartsAxis-directionX .MuiChartsAxis-tick': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-root': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-root *': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight *': { cursor: 'pointer' },
                '& .MuiChartsAxisHighlight-line, & .MuiChartsAxisHighlight-rect': {
                  cursor: 'pointer',
                },
                '& .MuiChartsAxisHighlightLine, & .MuiChartsAxisHighlightRect': {
                  cursor: 'pointer',
                },
                '& .MuiChartsOverlay-root': { cursor: 'pointer' },
                '& svg': { cursor: 'pointer' },
              }
            : null),
          ...(disableInteraction
            ? {
                '& .MuiChartsOverlay-root': { pointerEvents: 'none' },
                '& .MuiChartsAxisHighlight-root': { pointerEvents: 'none' },
                '& .MuiChartsAxisHighlight': { pointerEvents: 'none' },
                '& .MuiBarElement-root': { pointerEvents: 'none' },
              }
            : null),
        }}
      >
        {finiteReferenceLines.map((line, index) => (
          <ChartsReferenceLine
            key={`bar-guide-${line.value}-${line.color}-${index}`}
            y={line.value}
            lineStyle={{ stroke: line.color, strokeDasharray: '8 6', strokeWidth: 0.5 }}
          />
        ))}
      </MuiBarChart>
      {!hideLegend ? (
        <Box sx={{ display: 'grid', gap: s('xs'), marginTop: s('sm') }}>
          {series.map((item, index) => (
            <Box key={item.key} sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: s('xs') }}>
                <Box
                  sx={{
                    width: s('sm'),
                    height: s('sm'),
                    backgroundColor: resolveSeriesColor(item, index),
                  }}
                />
                <Text role="body">{item.label}</Text>
              </Box>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
};
